"use client";

import { createContext, useContext, type ReactNode } from "react";
import {
  useTableSearchParams,
  type UseTableSearchParamsConfig,
  type UseTableSearchParamsReturn,
} from "@/lib/hooks";
import type { FilterConfig } from "@/lib/types/data.types";

// ─── Context ─────────────────────────────────────────────────────────────────

const TablePageContext =
  createContext<UseTableSearchParamsReturn<FilterConfig> | null>(null);

// ─── Provider ────────────────────────────────────────────────────────────────

interface TablePageProviderProps {
  config: UseTableSearchParamsConfig;
  children: ReactNode;
}

export function TablePageProvider({
  config,
  children,
}: TablePageProviderProps) {
  const tableSearchParams = useTableSearchParams<FilterConfig>(config);

  return (
    <TablePageContext.Provider value={tableSearchParams}>
      {children}
    </TablePageContext.Provider>
  );
}

// ─── Consumer hook ───────────────────────────────────────────────────────────

export function useTablePage(): UseTableSearchParamsReturn<FilterConfig> {
  const context = useContext(TablePageContext);

  if (!context) {
    throw new Error("useTablePage must be used within a TablePageProvider");
  }

  return context;
}
